import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { FaCheck, FaSave, FaTrash, FaPalette } from 'react-icons/fa';
import StartGameOverlay from './StartGameOverlay';
import { useAuth0 } from '@auth0/auth0-react';
import { debounce } from 'lodash';
import ImageCard from './ImageCard';
import { toast } from 'react-hot-toast';
import RetroLoadingDots from './RetroLoadingDots';
import { getEmojisFromActiveSpices } from './SpiceRack';

const RetroFreeDrawing = ({ 
  isActive = false, 
  onEnd, 
  onGameComplete,
  showStartButton = false,
  generatedImage,
  componentId = 'unknown',
  skipSaveOnUnmount = false,
  isGenerating = false,
  activeSpices = []
}) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const colors = ['#FF00FF', '#FF6B00', '#00FF9F', '#FF1493', '#FFB800'];
  const [gameStarted, setGameStarted] = useState(false);
  const [waitingToStart, setWaitingToStart] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState(colors[Math.floor(Math.random() * colors.length)]);
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const canvasRef = useRef(null);
  const gameEndedRef = useRef(false);
  const hasSavedRef = useRef(false);
  const savingRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const internalIdRef = useRef(componentId === 'unknown' ? `drawing-${Date.now()}-${Math.random().toString(36).slice(2)}` : componentId);
  const stateRef = useRef({ gameStarted, paths, isActive });
  const [showRevealButton, setShowRevealButton] = useState(false);
  const [imageRevealed, setImageRevealed] = useState(false);
  const [saveOverlay, setSaveOverlay] = useState(null);

  // Reset state when component becomes active
  useEffect(() => {
    if (isActive) {
      setWaitingToStart(showStartButton);
      // Don't auto-start game if we're in generating state
      const shouldStartGame = !showStartButton && !isGenerating;
      setGameStarted(shouldStartGame);
      setPaths([]);
      setCurrentPath([]);
      setImageRevealed(false);
      setShowRevealButton(false);
      gameEndedRef.current = false;
      hasSavedRef.current = false;
      savingRef.current = false;
      

    }
  }, [isActive, showStartButton, isGenerating]);

  // Handle game start
  useEffect(() => {
    if (isActive && !waitingToStart && !isGenerating) {
      setGameStarted(true);

    }
  }, [isActive, waitingToStart, isGenerating]);

  // Handle transition from generating to game-ready
  useEffect(() => {
    if (isActive && !isGenerating && !gameStarted && !showStartButton) {

      setGameStarted(true);
    }
  }, [isActive, isGenerating, gameStarted, showStartButton]);

  // Save drawing to S3
  const saveDrawing = async () => {
    if (savingRef.current) return;
    savingRef.current = true;

    try {

      
      // Check server connectivity first
      try {
        const healthCheck = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/health`);
        if (!healthCheck.ok) {
          throw new Error(`Server health check failed: ${healthCheck.status}`);
        }

      } catch (healthError) {
        console.error(`[DRAW-DEBUG] ❌ Server is not reachable for ${internalIdRef.current}:`, healthError);
        throw new Error('Server is not reachable. Please check your connection.');
      }

      // Create a temporary canvas with transparency
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 1024; // Fixed size for consistency
      tempCanvas.height = 1024;
      const tempCtx = tempCanvas.getContext('2d');
      
      // Clear canvas with transparency
      tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Scale and draw the original canvas content
      const originalCanvas = canvasRef.current;
      const scale = Math.min(tempCanvas.width / originalCanvas.width, tempCanvas.height / originalCanvas.height);
      const scaledWidth = originalCanvas.width * scale;
      const scaledHeight = originalCanvas.height * scale;
      const x = (tempCanvas.width - scaledWidth) / 2;
      const y = (tempCanvas.height - scaledHeight) / 2;
      
      tempCtx.drawImage(originalCanvas, x, y, scaledWidth, scaledHeight);
      
      // Convert to PNG with transparency
      const drawingData = tempCanvas.toDataURL('image/png');

      let token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "retro-api",
          scope: "openid profile email"
        }
      });

      console.log(`[DRAW-DEBUG] 🔑 Got auth token for ${internalIdRef.current}:`, {
        tokenLength: token?.length,
        timestamp: new Date().toISOString()
      });

      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/drawings/save`;
      
      const payload = {
        drawingData,
        originalImageId: generatedImage?.id,
        userId: user?.sub,
        paths,
        componentId: internalIdRef.current,
        timestamp: new Date().toISOString()
      };

      console.log(`[DRAW-DEBUG] 🚀 Sending save request for ${internalIdRef.current}:`, {
        url: apiUrl,
        hasToken: !!token,
        payloadSize: drawingData.length,
        timestamp: new Date().toISOString()
      });
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[DRAW-DEBUG] ❌ Server returned error for ${internalIdRef.current}:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          timestamp: new Date().toISOString()
        });
        throw new Error(`Server error: ${response.status} ${errorText}`);
      }

      hasSavedRef.current = true;
      console.log(`[DRAW-DEBUG] ✅ Drawing saved successfully for ${internalIdRef.current}`);
      
      // Show success overlay
      setSaveOverlay(
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 flex items-center justify-center rounded-lg overflow-hidden"
          style={{ zIndex: 50 }}
        >
          {/* Semi-transparent backdrop with blur */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-[15px] text-white font-medium text-center font-['VT323']">
              Drawing saved ✨
            </div>
            <button
              onClick={() => {
                window.location.href = '/drawings';
              }}
              className="mt-2 text-[14px] text-white bg-white/10 hover:bg-white/20 active:bg-white/30 transition-colors duration-200 rounded px-4 py-2 font-['VT323']"
            >
              View in Drawings
            </button>
          </div>
        </motion.div>
      );

    } catch (error) {
      console.error(`[DRAW-DEBUG] ❌ Save failed for ${internalIdRef.current}:`, error);
      
      // Show error overlay
      setSaveOverlay(
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 flex items-center justify-center rounded-lg overflow-hidden"
          style={{ zIndex: 50 }}
        >
          {/* Semi-transparent backdrop with blur */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          {/* Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-[15px] text-white font-medium text-center font-['VT323']">
              Failed to save drawing: {error.message} ❌
            </div>
          </div>
        </motion.div>
      );

    } finally {
      savingRef.current = false;
      // Clear the overlay after a delay
      setTimeout(() => {
        setSaveOverlay(null);
      }, 2000);
    }
  };

  // Keep stateRef in sync
  useEffect(() => {
    stateRef.current = { gameStarted, paths, isActive };
  }, [gameStarted, paths, isActive]);

  // Component lifecycle logging
  useEffect(() => {
    console.log(`[DRAW-DEBUG] 🎮 Mount for ${internalIdRef.current}`);
    return () => {
      console.log(`[DRAW-DEBUG] 🎮 Unmount for ${internalIdRef.current}`);
    };
  }, []);

  // Set canvas size on mount and resize
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const parent = canvas.parentElement;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Show reveal button when image loads
  useEffect(() => {
    if (generatedImage) {
      const img = new Image();
      img.onload = () => {
        setShowRevealButton(true);
        if (!showStartButton) {
          setGameStarted(true);
          setWaitingToStart(false);
        }
      };
      img.src = generatedImage.url;
    }
  }, [generatedImage, showStartButton]);

  // Track state changes
  useEffect(() => {
    console.log(`[DRAW-DEBUG] 🎮 State changed for ${internalIdRef.current}:`, {
      isActive,
      gameStarted,
      waitingToStart,
      hasGeneratedImage: !!generatedImage,
      pathsCount: paths.length,
      gameEndedRef: gameEndedRef.current,
      componentId: internalIdRef.current,
      isGenerating,
      shouldShowGenerating: !gameStarted && isGenerating,
      timestamp: new Date().toISOString()
    });
  }, [isActive, gameStarted, waitingToStart, generatedImage, paths.length, isGenerating]);

  // Track generatedImage prop changes
  useEffect(() => {
    console.log(`[DRAW-DEBUG] 🖼️ Generated image prop changed for ${internalIdRef.current}:`, {
      hasGeneratedImage: !!generatedImage,
      imageDetails: generatedImage ? {
        id: generatedImage.id,
        url: generatedImage.url,
        type: typeof generatedImage
      } : null,
      gameStarted,
      pathsCount: paths.length,
      timestamp: new Date().toISOString()
    });
  }, [generatedImage]);

  // Remove the old save conditions and just track paths for debugging
  const debouncedCheckSaveCondition = useCallback(
    debounce(() => {
      if (!canvasRef.current) return;
      console.log(`[DRAW-DEBUG] 🎨 Path update for ${internalIdRef.current}:`, {
        pathsCount: paths.length,
        hasGeneratedImage: !!generatedImage,
        timestamp: new Date().toISOString()
      });
    }, 500),
    [paths.length, generatedImage]
  );

  // Loading card click handler
  const handleLoadingCardClick = () => {
    console.log(`[DRAW-DEBUG] 🎮 Loading card clicked for ${internalIdRef.current}:`, {
      waitingToStart,
      gameStarted,
      hasGeneratedImage: !!generatedImage,
      timestamp: new Date().toISOString()
    });
    setWaitingToStart(false);
    setGameStarted(true);
  };

  // Track drawing interactions
  const handleDrawStart = useCallback((e) => {
    if (!gameStarted) return;
    console.log(`[DRAW-DEBUG] 🎨 Drawing started for ${internalIdRef.current}:`, {
      gameStarted,
      pathsCount: paths.length,
      timestamp: new Date().toISOString()
    });
    setIsDrawing(true);
    setCurrentPath([]);
  }, [gameStarted, paths.length]);

  // Track drawing completion
  const handleDrawEnd = useCallback(() => {
    if (!gameStarted) return;
    if (currentPath.length > 0) {
      console.log(`[DRAW-DEBUG] 🎨 Path completed for ${internalIdRef.current}:`, {
        pathsCount: paths.length + 1,
        pointsInPath: currentPath.length,
        isActive,
        gameStarted,
        gameEndedRef: gameEndedRef.current,
        timestamp: new Date().toISOString()
      });
      setPaths(prev => [...prev, { points: currentPath, color: currentColor }]);
      setCurrentPath([]);
    }
    setIsDrawing(false);
  }, [gameStarted, currentPath, currentColor, paths.length, isActive]);

  // Start new path
  const handleMouseDown = useCallback((e) => {
    if (!gameStarted) return;
    
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!rect) return;

    setIsDrawing(true);
    
    // Get the correct position relative to the canvas
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Scale the position to match canvas dimensions
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Start new path with initial point
    setCurrentPath([{ 
      x: x * scaleX,
      y: y * scaleY
    }]);

    console.log(`[DRAW-DEBUG] 🎨 Drawing started for ${internalIdRef.current}:`, {
      gameStarted,
      pathsCount: paths.length,
      timestamp: new Date().toISOString()
    });
  }, [gameStarted, paths.length]);

  // End current path
  const handleMouseUp = useCallback(() => {
    if (!gameStarted) return;
    setIsDrawing(false);
    if (currentPath.length > 0) {
      setPaths(prev => [...prev, { points: currentPath, color: currentColor }]);
      setCurrentPath([]);
    }
  }, [gameStarted, currentPath, currentColor]);

  // Add points to current path
  const handleDraw = useCallback((e) => {
    if (!gameStarted || !isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!rect) return;

    // Get the correct position relative to the canvas
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Scale the position to match canvas dimensions
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    setCurrentPath(prev => [...prev, { 
      x: x * scaleX,
      y: y * scaleY
    }]);
  }, [gameStarted, isDrawing]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw completed paths
    paths.forEach(path => {
      if (path.points?.length) {
        ctx.beginPath();
        ctx.strokeStyle = path.color;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        path.points.forEach((point, i) => {
          if (i === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      }
    });

    // Draw current path
    if (currentPath.length > 0) {
      ctx.beginPath();
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      currentPath.forEach((point, i) => {
        if (i === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    }
  }, [paths, currentPath, currentColor]);

  // Handle image reveal
  const handleImageReveal = useCallback(() => {
    console.log(`[DRAW-DEBUG] 🎨 Image revealed for ${internalIdRef.current}:`, {
      pathsCount: paths.length,
      hasGeneratedImage: !!generatedImage,
      hasSaved: hasSavedRef.current,
      isSaving: savingRef.current,
      timestamp: new Date().toISOString()
    });
    setImageRevealed(true);
  }, [paths.length, generatedImage]);

  return (
    <div 
      className="relative w-full h-full aspect-square"
      onMouseEnter={() => {
        console.log(`[RETRO-DRAW] 🎨 Drawing area entered:`, {
          componentId: internalIdRef.current,
          isActive,
          gameStarted,
          timestamp: new Date().toISOString()
        });
      }}
      onMouseLeave={() => {
        console.log(`[RETRO-DRAW] 🎨 Drawing area left:`, {
          componentId: internalIdRef.current,
          isActive,
          gameStarted,
          timestamp: new Date().toISOString()
        });
      }}
    >
      {/* Drawing canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full bg-black rounded-xl"
        style={{ 
          touchAction: 'none',
          cursor: isDrawing ? 'none' : 'crosshair',
          zIndex: gameStarted ? 10 : 1  // Only bring canvas to front when game started
        }}
        onPointerDown={handleMouseDown}
        onPointerMove={handleDraw}
        onPointerUp={handleMouseUp}
        onMouseEnter={() => {
          console.log(`[RETRO] 🎨 Hover state changed for ${internalIdRef.current}:`, {
            isHovered: true,
            isDrawing,
            gameStarted,
            timestamp: new Date().toISOString()
          });
        }}
        onMouseLeave={() => {
          console.log(`[RETRO] 🎨 Hover state changed for ${internalIdRef.current}:`, {
            isHovered: false,
            isDrawing,
            gameStarted,
            timestamp: new Date().toISOString()
          });
        }}
      />

      {/* Generated image */}
      {generatedImage && (
        <div className="absolute inset-0 z-1">
          <ImageCard
            image={{
              ...generatedImage,
              id: generatedImage.id || 'placeholder-generating',
              prompt: generatedImage.prompt || 'Generating...',
              width: 1024,
              height: 1024
            }}
            disableBackgroundEffects={true}
            isGenerating={!imageRevealed}
            onImageLoaded={() => {
              console.log(`[DRAW-DEBUG] 🖼️ Generated image loaded for ${internalIdRef.current}`);
              setShowRevealButton(true);
            }}
          />
        </div>
      )}

      {/* Save Overlay */}
      <AnimatePresence>
        {saveOverlay && saveOverlay}
      </AnimatePresence>

      {/* Controls */}
      {gameStarted && (
        <div className="absolute top-2 right-2 flex gap-2 z-10">
          {/* Save button */}
          {paths.length > 0 && !hasSavedRef.current && (
            <button
              onClick={saveDrawing}
              disabled={savingRef.current}
              className="p-2 transition-transform hover:scale-110"
            >
              <FaSave className={`w-4 h-4 text-white ${savingRef.current ? 'opacity-50' : 'opacity-80 hover:opacity-100'}`} />
            </button>
          )}

          {/* Color palette */}
          <div className="flex h-2">
            {colors.map(color => (
              <button
                key={color}
                className={`flex-1 h-full transition-all ${
                  currentColor === color ? 'opacity-100' : 'opacity-60 hover:opacity-80'
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setCurrentColor(color)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Generating Animation - show when generating but not started drawing */}
      <AnimatePresence>
        {(() => {
          const shouldShow = !gameStarted && isGenerating;
          console.log(`[DRAW-DEBUG] 🎯 Generating animation render check for ${internalIdRef.current}:`, {
            gameStarted,
            isGenerating,
            shouldShow,
            timestamp: new Date().toISOString()
          });
          return shouldShow;
        })() && (
          <motion.div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            style={{ zIndex: 5 }}
            onClick={() => {
              console.log(`[DRAW-DEBUG] 🎮 Generating animation clicked for ${internalIdRef.current}`);
              setWaitingToStart(false);
              setGameStarted(true);
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <RetroLoadingDots 
              text="Generating" 
              size="28px" 
              className="text-white" 
              spiceEmojis={(() => {
                const emojis = getEmojisFromActiveSpices(activeSpices);
                console.log('🌶️ [DEBUG] RetroFreeDrawing placeholder emojis:', {
                  activeSpices,
                  emojis,
                  activeSpicesLength: activeSpices.length,
                  componentId
                });
                return emojis;
              })()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Start overlay */}
      {waitingToStart && showStartButton && (
        <div className="absolute inset-0 z-3">
          <StartGameOverlay onStart={() => setWaitingToStart(false)} />
        </div>
      )}
    </div>
  );
};

export default RetroFreeDrawing; 